import classNames from 'classnames';
import { FormikErrors } from 'formik';
import React, { CSSProperties, useCallback, useState } from 'react';

import UploadPhotoButton from '../upload-photo/UploadPhotoButton';
import Error from './Error';
import { BiSolidPencil } from 'react-icons/bi';

export enum UploadAspectRatios {
  OneToOne = 1,
  FourToFive = 4 / 5,
  ThreeToTwo = 3 / 2,
  NineToSixteen = 9 / 16
}

const UploadPhoto = ({
  preview,
  onClick,
  photoUrl,
  label,
  maxLabel,
  aspectRatio,
  disabled,
  buttonStyle = {},
  defaultIconSize
}) => (
  <button
    type="button"
    onClick={onClick}
    className="w-full h-full"
    disabled={disabled}
  >
    {preview || photoUrl ? (
      <img
        src={preview || photoUrl}
        className="w-full object-cover object-center"
      />
    ) : (
      <div
        className={classNames(
          'w-full h-full bg-primary-200 text-center flex flex-col items-center justify-center'
        )}
        style={buttonStyle}
      >
        <p
          className={classNames(
            !defaultIconSize &&
              aspectRatio === UploadAspectRatios.OneToOne &&
              'text-3xl',
            !defaultIconSize &&
              aspectRatio !== UploadAspectRatios.OneToOne &&
              'text-6xl'
          )}
          style={defaultIconSize ? { fontSize: defaultIconSize } : {}}
        >
          🖼
        </p>
        <p
          className={classNames(
            'text-xs decoration-[#575757] font-light',
            aspectRatio === UploadAspectRatios.OneToOne
              ? 'mt-0 w-[100%] text-xs'
              : 'w-[140px] md:px-4 mt-3',
            buttonStyle && 'w-[100%] mt-1'
          )}
        >
          {label || '4:5'}
        </p>
        {maxLabel !== null && (
          <p className="text-xs decoration-[#575757] font-semibold">
            {maxLabel || 'Event Poster*'}
          </p>
        )}
      </div>
    )}
  </button>
);

interface ImageUploadProps {
  label?: string;
  maxLabel?: string;
  className?: string;
  aspectRatio?: UploadAspectRatios;
  onUploadPhoto: (blob: Blob) => void;
  photoUrl?: string;
  disabled?: boolean;
  isUploading?: boolean;
  editIconOnly?: boolean;
  error?: string | FormikErrors<any> | string[] | FormikErrors<any>[];
  buttonStyle?: CSSProperties;
  defaultIconSize?: number;
  onSetError?: (error: string) => void;
}

const EditImageIcon = ({ onClick }: { onClick?: () => void }) => (
  <div className="absolute right-0">
    <div
      className="mr-2 mt-2 rounded-full p-1 bg-blue-200 w-[32px] h-[32px] flex items-center justify-center"
      style={{ backgroundColor: '#EEF1FF' }}
      onClick={() => onClick?.()}
    >
      <BiSolidPencil size={18} color="#3853C4" />
    </div>
  </div>
);

const ImageUpload: React.FC<ImageUploadProps> = ({
  label,
  maxLabel,
  className,
  aspectRatio = UploadAspectRatios.FourToFive,
  onUploadPhoto,
  isUploading,
  photoUrl,
  error,
  onSetError,
  disabled,
  buttonStyle,
  defaultIconSize,
  editIconOnly
}) => {
  const [preview, setPreview] = useState<string>('');

  const onUploadPhotoButtonClicked = (blob) => {
    setPreview(URL.createObjectURL(blob));
    onUploadPhoto(blob);
  };

  const WrappedButton = useCallback(
    ({ onClick }) => (
      <UploadPhoto
        onClick={onClick}
        photoUrl={photoUrl}
        preview={preview}
        maxLabel={maxLabel}
        disabled={disabled}
        aspectRatio={aspectRatio}
        label={label}
        buttonStyle={buttonStyle}
        defaultIconSize={defaultIconSize}
      />
    ),
    [preview, photoUrl, disabled, buttonStyle, defaultIconSize]
  );

  return (
    <>
      <div
        className={classNames(className, {
          'animate-pulse': isUploading
        })}
      >
        <div
          className={classNames(
            'md:mx-auto relative transition-transform',
            !editIconOnly && 'hover:scale-105'
          )}
        >
          {photoUrl && !editIconOnly && (
            <UploadPhotoButton
              cropShape="rect"
              aspectRatio={aspectRatio}
              ButtonComponent={EditImageIcon}
              uploadPhoto={onUploadPhotoButtonClicked}
            />
          )}

          {photoUrl && editIconOnly && <EditImageIcon />}

          {(!photoUrl || (photoUrl && !editIconOnly)) && (
            <div
              className={classNames(
                'rounded-lg shadow-xl overflow-hidden',
                {
                  'border-1 border-red-700': !!error
                },
                aspectRatio === UploadAspectRatios.OneToOne && 'rounded-2xl',
                aspectRatio === UploadAspectRatios.OneToOne &&
                  !error &&
                  'border-white border-2',
                aspectRatio === UploadAspectRatios.OneToOne &&
                  !!error &&
                  'border-red-700 border-2'
              )}
              style={
                buttonStyle || {
                  aspectRatio: aspectRatio.toString()
                }
              }
            >
              <UploadPhotoButton
                cropShape="rect"
                aspectRatio={aspectRatio}
                ButtonComponent={WrappedButton}
                uploadPhoto={onUploadPhotoButtonClicked}
                onSetError={onSetError}
              />
            </div>
          )}
          <Error error={error} />
        </div>
      </div>
    </>
  );
};

export default ImageUpload;
